import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    IconButton,
    Badge,
    Chip,
    Button,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import { formatJuryScore, resolveAwardTierDisplayLabel } from './submissionObsidianShared';
import {
    Search as SearchIcon,
    Sort as SortIcon,
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    Star as MedalIcon,
    Grade as AwardIcon,
    Diamond as GiftIcon,
    Close,
} from '@mui/icons-material';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IAwardCategory, IManageContestSubmissions, TSortBy, TCampSortOrder } from '../../../../redux/contest/types';
import { IContest } from '../../../../types/global/helper';
import { getIdForIbjects, showToastMessage } from '../../../../utils/AppUtils';
import { formatAwardPrizeForDisplay } from '../../../../pages/BrandDashboard/contestWizard/buildAwardCategoryPayload';
import { v4 as uuidv4 } from 'uuid';
import {
    midnight,
    editorialHeadlineSx,
    editorialSelectMenuProps,
} from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

/** Matches submission list page — deep page bg + elevated card surfaces. */
const PRIZE_MODAL_BG = '#0a0a0a';
const PRIZE_MODAL_CARD = '#121212';
const PRIZE_MODAL_BORDER = 'rgba(255, 255, 255, 0.08)';
const PRIZE_MODAL_CHIP_BG = '#1a1a1a';
const AMBER_BORDER_SUBTLE = 'rgba(255, 181, 71, 0.2)';
const PRIZE_CARD_HOVER_GRADIENT = `linear-gradient(135deg, rgba(255, 201, 101, 0.1) 0%, ${PRIZE_MODAL_CARD} 100%)`;

const prizeModalPaperSx = {
    bgcolor: PRIZE_MODAL_BG,
    border: `1px solid ${PRIZE_MODAL_BORDER}`,
    borderRadius: '8px',
    minHeight: '85vh',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
};

const prizeModalHeaderSx = {
    px: 4,
    py: 3,
    borderBottom: `1px solid ${PRIZE_MODAL_BORDER}`,
    bgcolor: PRIZE_MODAL_BG,
    ...editorialHeadlineSx,
    fontSize: '1.5rem',
    color: midnight.onSurface,
};

const prizeModalFilterInputSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        bgcolor: PRIZE_MODAL_CARD,
        borderRadius: '8px',
        color: midnight.onSurface,
        fontFamily: FONT_ENUM.MAIN_REGULAR,
        fontSize: '0.875rem',
        transition: 'all 0.2s ease',
        '& fieldset': { borderColor: PRIZE_MODAL_BORDER },
        '&:hover fieldset': { borderColor: PRIZE_MODAL_BORDER },
        '&.Mui-focused fieldset': {
            borderColor: midnight.primary,
            borderWidth: '1px',
        },
        '& .MuiInputBase-input::placeholder': {
            color: midnight.onSurfaceVariant,
            opacity: 1,
        },
    },
};

const prizeModalFilterLabelSx: SxProps<Theme> = {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'rgba(255, 201, 101, 0.8)',
    ml: 0.5,
    mb: 0.75,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
};

const prizeModalSelectSx: SxProps<Theme> = {
    ...prizeModalFilterInputSx,
    '& .MuiSelect-select': {
        py: 1.25,
        px: 1.5,
        fontSize: '0.875rem',
        fontFamily: FONT_ENUM.MAIN_REGULAR,
    },
    '& .MuiSvgIcon-root': {
        color: midnight.onSurfaceVariant,
    },
};

const prizeModalNeutralChipSx: SxProps<Theme> = {
    height: 'auto',
    bgcolor: PRIZE_MODAL_CHIP_BG,
    color: '#d1d5db',
    border: `1px solid ${PRIZE_MODAL_BORDER}`,
    borderRadius: '9999px',
    fontSize: 10,
    fontWeight: 700,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    '& .MuiChip-label': { px: 1.5, py: 0.5 },
};

const prizeModalAccentChipSx: SxProps<Theme> = {
    ...prizeModalNeutralChipSx,
    bgcolor: PRIZE_MODAL_CARD,
    color: midnight.primary,
    border: `1px solid ${AMBER_BORDER_SUBTLE}`,
};

interface PrizeAward {
    award_category: string;
    award_project: string;
    award_status: 'won' | 'nominated';
    award_year: string;
    person_awarded: string;
    contest: string;
    requestId?: string;
}

interface PrizeAwardModalProps {
    open: boolean;
    onClose: () => void;
    awardCategories: IAwardCategory[];
    submissions: IManageContestSubmissions[];
    contest: IContest;
    existingAwards?: any; // IManagedAwards from the manage response
    onAward: (awards: PrizeAward[]) => void;
}

// Sort options for submissions
const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: 'Oldest First' },
    { value: 'createdAt_asc', label: 'Newest First' },
    { value: 'votes_desc', label: 'Most Votes' },
    { value: 'votes_asc', label: 'Least Votes' },
    { value: 'jury_score_desc', label: 'Highest Jury Score' },
    { value: 'jury_score_asc', label: 'Lowest Jury Score' },
];

const PRIZE_ICONS = {
    trophy: TrophyIcon,
    medal: MedalIcon,
    award: AwardIcon,
    gift: GiftIcon,
};

export const PrizeAwardModal: React.FC<PrizeAwardModalProps> = ({
    open,
    onClose,
    awardCategories,
    submissions,
    contest,
    existingAwards,
    onAward,
}) => {
    const [draggedPrize, setDraggedPrize] = useState<IAwardCategory | null>(null);
    const [awards, setAwards] = useState<PrizeAward[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [sortOrder, setSortOrder] = useState<TCampSortOrder | undefined>();
    const [sortBy, setSortBy] = useState<TSortBy | undefined>();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [dropTarget, setDropTarget] = useState<string | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [preloadedAwardIds, setPreloadedAwardIds] = useState<Set<string>>(new Set());

    // Preload existing awards when modal opens
    useEffect(() => {
        if (open && existingAwards?.awards) {
            const preloadedAwards: PrizeAward[] = [];
            const preloadedIds = new Set<string>();

            // Flatten all awards from the manage response
            existingAwards.awards.forEach((award: any) => {
                const awardId = award.award_category?._id || award.award_category;
                const projectId = award.award_project?._id || award.award_project;

                preloadedAwards.push({
                    award_category: awardId,
                    award_project: projectId,
                    award_status: award.award_status || 'won',
                    award_year: award.award_year || award.created_at,
                    person_awarded: award.person_awarded?._id || award.person_awarded,
                    contest: award.contest?._id || award.contest,
                    requestId: award.requestId || uuidv4(),
                });

                // Track this as a preloaded award (combination of award category and project)
                preloadedIds.add(`${awardId}-${projectId}`);
            });

            setAwards(preloadedAwards);
            setPreloadedAwardIds(preloadedIds);
        } else if (open) {
            // Reset awards when modal opens without existing awards
            setAwards([]);
            setPreloadedAwardIds(new Set());
        }
    }, [open, existingAwards]);

    // Get unique categories for filter
    const categories = useMemo(() => {
        if (!contest.categories_enabled || !contest.categories) return [];
        return contest.categories.map(cat => cat.category_name);
    }, [contest.categories, contest.categories_enabled]);

    // Filter and sort submissions
    const filteredSubmissions = useMemo(() => {
        let filtered = submissions.filter(submission => {
            const matchesSearch =
                submission.project?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                submission.created_by?.userName?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || submission.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        // Apply sorting
        if (sortBy && sortOrder) {
            filtered.sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortBy) {
                    case 'createdAt':
                        aValue = new Date(a.createdAt).getTime();
                        bValue = new Date(b.createdAt).getTime();
                        break;
                    case 'votes':
                        aValue = a.totalVotes || 0;
                        bValue = b.totalVotes || 0;
                        break;
                    case 'jury_score':
                        aValue = a.averageJuryScore || 0;
                        bValue = b.averageJuryScore || 0;
                        break;
                    default:
                        return 0;
                }

                if (sortOrder === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
        }

        return filtered;
    }, [submissions, searchQuery, selectedCategory, sortBy, sortOrder]);

    // Check if prize is already awarded (from modal state or existing submissions)
    const isPrizeAwarded = (prizeId: string) => {
        // Check if prize is in modal state
        const inModalState = awards.some(award => award.award_category === prizeId);

        // Check if prize is already awarded to any submission
        const inExistingSubmissions = submissions.some(
            submission =>
                submission.awards && submission.awards.some(award => getIdForIbjects(award.award_category) === prizeId),
        );

        return inModalState || inExistingSubmissions;
    };

    // Get prizes awarded to submission (now returns array for multiple prizes)
    const getSubmissionPrizes = (submissionId: string) => {
        const submissionAwards = awards.filter(a => a.award_project === submissionId);
        return submissionAwards.map(award => awardCategories.find(p => p._id === award.award_category)).filter(Boolean);
    };

    // Check if submission already has awards from the submission's awards field
    const getSubmissionExistingAwards = (submission: IManageContestSubmissions) => {
        if (submission.awards && Array.isArray(submission.awards)) {
            return submission.awards
                .map(award => awardCategories.find(p => p._id === getIdForIbjects(award.award_category)))
                .filter(Boolean);
        }
        return [];
    };

    // Get all prizes for a submission (from both modal state and submission's awards field)
    const getAllSubmissionPrizes = (submission: IManageContestSubmissions) => {
        const modalAwards = getSubmissionPrizes(submission.project._id);
        const existingAwards = getSubmissionExistingAwards(submission);

        // Combine and deduplicate
        const allPrizes = [...modalAwards, ...existingAwards];
        const uniquePrizes = allPrizes.filter(
            (prize, index, self) => index === self.findIndex(p => p?._id === prize?._id),
        );

        return uniquePrizes;
    };

    // Handle drag start
    const handleDragStart = (prize: IAwardCategory) => {
        if (!isPrizeAwarded(prize._id)) {
            setDraggedPrize(prize);
        }
    };

    // Handle drag end
    const handleDragEnd = () => {
        setDraggedPrize(null);
        setDropTarget(null);
    };

    // Handle drag over
    const handleDragOver = (e: React.DragEvent, submissionId: string) => {
        e.preventDefault();
        if (draggedPrize) {
            setDropTarget(submissionId);
        }
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setDropTarget(null);
    };

    // Handle drop
    const handleDrop = (e: React.DragEvent, submission: IManageContestSubmissions) => {
        const { project, created_by } = submission;
        e.preventDefault();
        if (draggedPrize) {
            // Validate tier matching
            const prizeTierName = resolveAwardTierDisplayLabel(draggedPrize, contest);
            const submissionTierLabel = submission.chosen_tier?.label;

            // If both tier and chosen_tier exist, they must match
            if (prizeTierName && submissionTierLabel && prizeTierName !== submissionTierLabel) {
                showToastMessage(
                    `This prize is for tier "${prizeTierName}" but the submission was made to tier "${submissionTierLabel}". Please select a prize that matches the submission tier.`,
                    'error',
                );
                setDraggedPrize(null);
                setDropTarget(null);
                return;
            }

            setAwards([
                ...awards,
                {
                    award_category: draggedPrize._id,
                    award_project: project._id,
                    award_status: 'won',
                    award_year: new Date().toISOString(),
                    person_awarded: created_by._id,
                    contest: getIdForIbjects(contest),
                    requestId: uuidv4(),
                },
            ]);
        }
        setDraggedPrize(null);
        setDropTarget(null);
    };

    // Remove award (only allow removal of new awards, not preloaded ones)
    const removeAward = (submissionId: string, prizeId?: string) => {
        if (prizeId) {
            // Check if this is a preloaded award
            const awardKey = `${prizeId}-${submissionId}`;
            if (preloadedAwardIds.has(awardKey)) {
                // Don't allow removal of preloaded awards
                return;
            }

            // Remove specific prize from submission (only new awards)
            setAwards(
                awards.filter(award => !(award.award_project === submissionId && award.award_category === prizeId)),
            );
        } else {
            // Remove all non-preloaded prizes from submission
            setAwards(
                awards.filter(award => {
                    if (award.award_project !== submissionId) return true;
                    const awardKey = `${award.award_category}-${submissionId}`;
                    return !preloadedAwardIds.has(awardKey); // Keep preloaded awards
                }),
            );
        }
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        const [field, order] = value.split('_');
        setSortOrder(order as TCampSortOrder);
        setSortBy(field as TSortBy);
    };

    // Handle category change
    const handleCategoryChange = (event: SelectChangeEvent<string>) => {
        setSelectedCategory(event.target.value);
    };

    // Handle submit - show confirmation dialog first
    const handleSubmit = () => {
        setConfirmDialogOpen(true);
    };

    // Handle confirmed submit
    const handleConfirmedSubmit = () => {
        onAward(awards);
        setAwards([]);
        setConfirmDialogOpen(false);
        onClose();
    };

    // Handle cancel confirmation
    const handleCancelConfirmation = () => {
        setConfirmDialogOpen(false);
    };

    // Check if an award is preloaded (cannot be removed)
    const isPreloadedAward = (submissionId: string, prizeId: string) => {
        const awardKey = `${prizeId}-${submissionId}`;
        return preloadedAwardIds.has(awardKey);
    };

    // Get icon for prize
    const getPrizeIcon = (prize: IAwardCategory) => {
        // Default to trophy if no specific icon mapping
        const iconKey = prize.category_title.toLowerCase().includes('gold')
            ? 'trophy'
            : prize.category_title.toLowerCase().includes('silver')
            ? 'medal'
            : prize.category_title.toLowerCase().includes('bronze')
            ? 'award'
            : 'gift';
        return PRIZE_ICONS[iconKey as keyof typeof PRIZE_ICONS] || TrophyIcon;
    };

    const getPrizeColor = () => midnight.primary;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: prizeModalPaperSx }}>
                <DialogTitle sx={prizeModalHeaderSx}>Award Prizes</DialogTitle>

                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(85vh - 88px)',
                        bgcolor: PRIZE_MODAL_BG,
                    }}>
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Left Panel - Prizes */}
                        <Box
                            sx={{
                                width: 320,
                                flexShrink: 0,
                                borderRight: `1px solid ${PRIZE_MODAL_BORDER}`,
                                bgcolor: PRIZE_MODAL_BG,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}>
                            <Typography
                                sx={{
                                    px: 3,
                                    pt: 3,
                                    pb: 2,
                                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                    fontSize: '1.125rem',
                                    fontWeight: 600,
                                    color: midnight.onSurface,
                                }}>
                                Available Prizes
                            </Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    px: 3,
                                    pb: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                }}>
                                {awardCategories.map(prize => {
                                    const tierDisplayLabel = resolveAwardTierDisplayLabel(prize, contest);
                                    const Icon = getPrizeIcon(prize);
                                    const isAwarded = isPrizeAwarded(prize._id);
                                    const isDragging = draggedPrize?._id === prize._id;

                                    return (
                                        <Box
                                            key={prize._id}
                                            draggable={!isAwarded}
                                            onDragStart={() => handleDragStart(prize)}
                                            onDragEnd={handleDragEnd}
                                            sx={{
                                                p: 2,
                                                borderRadius: '12px',
                                                border: `1px solid ${
                                                    isAwarded
                                                        ? PRIZE_MODAL_BORDER
                                                        : isDragging
                                                        ? midnight.primary
                                                        : PRIZE_MODAL_BORDER
                                                }`,
                                                background: isDragging ? PRIZE_CARD_HOVER_GRADIENT : PRIZE_MODAL_CARD,
                                                opacity: isAwarded ? 0.5 : 1,
                                                cursor: isAwarded ? 'not-allowed' : 'grab',
                                                transition: 'border-color 0.2s ease, background 0.2s ease',
                                                '&:hover': !isAwarded
                                                    ? {
                                                          borderColor: midnight.primary,
                                                          background: PRIZE_CARD_HOVER_GRADIENT,
                                                      }
                                                    : {},
                                                position: 'relative',
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: '8px',
                                                        bgcolor: isAwarded
                                                            ? PRIZE_MODAL_CHIP_BG
                                                            : 'rgba(255, 201, 101, 0.2)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}>
                                                    <Icon
                                                        sx={{
                                                            color: isAwarded
                                                                ? midnight.onSurfaceVariant
                                                                : midnight.primary,
                                                            fontSize: 24,
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 14,
                                                            color: isAwarded
                                                                ? midnight.onSurfaceVariant
                                                                : midnight.onSurface,
                                                            mb: 0.5,
                                                        }}>
                                                        {prize.category_title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '1.25rem',
                                                            fontWeight: 700,
                                                            color: isAwarded ? midnight.onSurface : midnight.primary,
                                                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                                        }}>
                                                        {formatAwardPrizeForDisplay(prize)}
                                                    </Typography>
                                                    {tierDisplayLabel && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10,
                                                                color: 'rgba(255, 201, 101, 0.8)',
                                                                mt: 0.5,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.1em',
                                                            }}>
                                                            Tier: {tierDisplayLabel}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            {isAwarded && (
                                                <Badge
                                                    badgeContent="Awarded"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        '& .MuiBadge-badge': {
                                                            bgcolor: midnight.primary,
                                                            color: '#392700',
                                                            fontSize: 10,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Right Panel - Submissions */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                bgcolor: PRIZE_MODAL_BG,
                            }}>
                            {/* Search and Filters */}
                            <Box
                                sx={{
                                    p: 3,
                                    borderBottom: `1px solid ${PRIZE_MODAL_BORDER}`,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    alignItems: 'flex-end',
                                }}>
                                <Box sx={{ flex: 1, minWidth: 240 }}>
                                    <TextField
                                        placeholder="Search by title or username"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        fullWidth
                                        sx={prizeModalFilterInputSx}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon
                                                        sx={{ color: midnight.onSurfaceVariant, fontSize: 20 }}
                                                    />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchQuery ? (
                                                <InputAdornment position="end">
                                                    <CloseIcon
                                                        sx={{
                                                            color: midnight.onSurfaceVariant,
                                                            cursor: 'pointer',
                                                            fontSize: 18,
                                                        }}
                                                        onClick={() => setSearchQuery('')}
                                                    />
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                </Box>
                                {contest?.categories_enabled && categories.length > 0 && (
                                    <Box sx={{ width: { xs: '100%', md: 192 } }}>
                                        <Typography sx={prizeModalFilterLabelSx}>Category</Typography>
                                        <FormControl fullWidth sx={prizeModalSelectSx}>
                                            <Select
                                                value={selectedCategory}
                                                onChange={handleCategoryChange}
                                                displayEmpty
                                                MenuProps={editorialSelectMenuProps}>
                                                <MenuItem value="all">All Categories</MenuItem>
                                                {categories.map(category => (
                                                    <MenuItem key={category} value={category}>
                                                        {category}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                                <Box sx={{ width: { xs: '100%', md: 192 } }}>
                                    <Typography sx={prizeModalFilterLabelSx}>Sort By</Typography>
                                    <FormControl fullWidth sx={prizeModalSelectSx}>
                                        <Select
                                            value={sortOption}
                                            onChange={handleSortChange}
                                            MenuProps={editorialSelectMenuProps}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <SortIcon sx={{ color: midnight.onSurfaceVariant, fontSize: 18 }} />
                                                </InputAdornment>
                                            }>
                                            {SORT_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>

                            {/* Submissions List */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    p: 3,
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {filteredSubmissions.map(submission => {
                                        const submissionPrizes = getAllSubmissionPrizes(submission);
                                        const isDropping = dropTarget === submission._id;

                                        return (
                                            <Box
                                                key={submission._id}
                                                onDragOver={e => handleDragOver(e, submission._id)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={e => handleDrop(e, submission)}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: '8px',
                                                    bgcolor: PRIZE_MODAL_CARD,
                                                    border: `1px solid ${
                                                        isDropping
                                                            ? midnight.primary
                                                            : submissionPrizes.length > 0
                                                            ? AMBER_BORDER_SUBTLE
                                                            : PRIZE_MODAL_BORDER
                                                    }`,
                                                    transition: 'all 0.2s ease',
                                                    transform: isDropping ? 'scale(1.01)' : 'scale(1)',
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255, 181, 71, 0.4)',
                                                    },
                                                    '&:hover .prize-submission-title': {
                                                        color: midnight.primary,
                                                    },
                                                }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                                        <Box
                                                            component="img"
                                                            src={
                                                                submission.project?.feature_image || '/placeholder.svg'
                                                            }
                                                            alt={submission.project?.title}
                                                            sx={{
                                                                width: 96,
                                                                height: 96,
                                                                borderRadius: '8px',
                                                                objectFit: 'cover',
                                                                border: `1px solid ${PRIZE_MODAL_BORDER}`,
                                                            }}
                                                        />
                                                        {submissionPrizes.length > 0 && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: -4,
                                                                    right: -4,
                                                                    display: 'flex',
                                                                    gap: 0.5,
                                                                }}>
                                                                {submissionPrizes.map((prize, index) => (
                                                                    <Box
                                                                        key={prize?._id || index}
                                                                        sx={{
                                                                            p: 0.5,
                                                                            borderRadius: '50%',
                                                                            bgcolor: getPrizeColor(),
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }}>
                                                                        {(() => {
                                                                            const Icon = getPrizeIcon(prize!);
                                                                            return (
                                                                                <Icon
                                                                                    sx={{
                                                                                        fontSize: 12,
                                                                                        color: '#392700',
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            className="prize-submission-title"
                                                            sx={{
                                                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                                                fontSize: '1.125rem',
                                                                fontWeight: 700,
                                                                color: midnight.onSurface,
                                                                mb: 0.5,
                                                                transition: 'color 0.2s ease',
                                                            }}>
                                                            {submission.project?.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                color: midnight.onSurfaceVariant,
                                                                mb: 1.5,
                                                            }}>
                                                            by{' '}
                                                            <Box
                                                                component="span"
                                                                sx={{ color: 'rgba(255, 201, 101, 0.8)' }}>
                                                                @{submission.created_by?.userName || 'Unknown User'}
                                                            </Box>
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={`${submission.totalVotes || 0} votes`}
                                                                size="small"
                                                                sx={prizeModalNeutralChipSx}
                                                            />
                                                            <Chip
                                                                label={`${formatJuryScore(
                                                                    submission.averageJuryScore,
                                                                )} jury score`}
                                                                size="small"
                                                                sx={prizeModalNeutralChipSx}
                                                            />
                                                            {submission.category && (
                                                                <Chip
                                                                    label={submission.category}
                                                                    size="small"
                                                                    sx={prizeModalAccentChipSx}
                                                                />
                                                            )}
                                                            {submission.chosen_tier?.label && (
                                                                <Chip
                                                                    label={submission.chosen_tier.label}
                                                                    size="small"
                                                                    sx={prizeModalNeutralChipSx}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {submissionPrizes.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1,
                                                                minWidth: 200,
                                                            }}>
                                                            {submissionPrizes.map((prize, index) => (
                                                                <Box
                                                                    key={prize?._id || index}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 2,
                                                                    }}>
                                                                    <Box sx={{ textAlign: 'right', flex: 1 }}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 12,
                                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                                color: midnight.onSurface,
                                                                            }}>
                                                                            {prize?.category_title}
                                                                        </Typography>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 10,
                                                                                color: midnight.onSurfaceVariant,
                                                                            }}>
                                                                            {formatAwardPrizeForDisplay(prize!)}
                                                                        </Typography>
                                                                    </Box>
                                                                    {!isPreloadedAward(
                                                                        submission.project._id,
                                                                        prize?._id || '',
                                                                    ) && (
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                removeAward(
                                                                                    submission.project._id,
                                                                                    prize?._id,
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                color: midnight.onSurfaceVariant,
                                                                                '&:hover': { color: midnight.primary },
                                                                            }}>
                                                                            <Close />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>

                                                {isDropping && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: 'rgba(255, 201, 101, 0.2)',
                                                            borderRadius: '8px',
                                                            pointerEvents: 'none',
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                color: midnight.primary,
                                                            }}>
                                                            Drop to award prize
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            px: 4,
                            py: 2.5,
                            borderTop: `1px solid ${PRIZE_MODAL_BORDER}`,
                            bgcolor: PRIZE_MODAL_BG,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: midnight.onSurfaceVariant,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            <Box component="span" sx={{ color: midnight.primary, fontWeight: 700 }}>
                                {awards.length}
                            </Box>{' '}
                            {awards.length === 1 ? 'prize' : 'prizes'} awarded
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                onClick={onClose}
                                sx={{
                                    px: 4,
                                    py: 1.25,
                                    borderRadius: '8px',
                                    border: `1px solid ${PRIZE_MODAL_BORDER}`,
                                    color: '#d1d5db',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: PRIZE_MODAL_CARD },
                                }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                disabled={awards.length === 0}
                                onClick={handleSubmit}
                                sx={{
                                    px: 4,
                                    py: 1.25,
                                    borderRadius: '8px',
                                    bgcolor: midnight.primary,
                                    color: '#392700',
                                    fontWeight: 800,
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    boxShadow: '0 10px 15px -3px rgba(255, 201, 101, 0.2)',
                                    '&:hover': { bgcolor: '#e6b55a' },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(255, 201, 101, 0.35)',
                                        color: 'rgba(57, 39, 0, 0.5)',
                                    },
                                }}>
                                Confirm Awards
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <DialogueSheet
                open={confirmDialogOpen}
                onClose={handleCancelConfirmation}
                onAgreeClicked={handleConfirmedSubmit}
                onDisagreeClick={handleCancelConfirmation}
                positiveText="Yes, Confirm Awards"
                negativeText="Cancel"
                header="Confirm Award Assignment"
                body="Are you sure you want to assign these awards? This action cannot be reversed and will permanently assign the selected prizes to the submissions."
            />
        </>
    );
};
